import React, { Component } from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import { translationPropType } from '@eventbrite/i18n';

import {
    CROP_THUMB_DIRECTIONS,
    DIRECTION_TO_CLASSNAME_MAP,
    CROP_MASK_PROP_TYPE,
} from './constants';
import {
    formatPercentCropMaskForPolygon,
    formatPercentCropMaskForSVG,
    formatPercentCropMaskForStyle,
    constrainCropMask,
    cropMaskToPercent,
    getDefaultCropMask,
    getScale,
    addMoveListener,
    addActionEndListener,
    removeMoveListener,
    removeActionEndListener,
    getNextMovedCropMask,
    getNextResizedCropMask,
    shouldUpdateCropMask,
    cropMaskDidChange,
} from './utils';
import { getEventPosition } from '@eventbrite/eds-utils';

import './imageCropper.scss';

const ImageCropperMask = ({ percentCropMask }) => {
    if (!percentCropMask) {
        return null;
    }

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="eds-image-cropper__cover"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
        >
            <polygon
                className="eds-image-cropper__mask"
                points={formatPercentCropMaskForPolygon(percentCropMask)}
            />
            <rect
                className="eds-image-cropper__ants"
                vectorEffect="non-scaling-stroke"
                {...formatPercentCropMaskForSVG(percentCropMask)}
            />
        </svg>
    );
};

const ImageCropperPorthole = ({
    percentCropMask,
    thumbMouseDown,
    portholeMouseDown,
}) => {
    if (!percentCropMask) {
        return null;
    }

    const _handleThumbMouseDown = (direction, event) => {
        // preventDefault to stop text-selection while dragging
        event.preventDefault();
        thumbMouseDown(getEventPosition(event), direction);
    };
    const _handlePortholeMouseDown = (event) => {
        // if the mousedown occurred on a resize thumb, ignore it
        // do this using data attr rather than stopPropagation
        if (event.target.getAttribute('data-cropper-thumb')) {
            return;
        }

        event.preventDefault();
        portholeMouseDown(getEventPosition(event));
    };

    // TODO EB-57935: We need to make the ImageCropper accessible by providing keyboard support.
    // It currently only works with mouse/touch
    /* eslint-disable jsx-a11y/no-static-element-interactions */
    const thumbs = CROP_THUMB_DIRECTIONS.map((direction) => (
        <div
            key={direction}
            className={`eds-image-cropper__thumb ${DIRECTION_TO_CLASSNAME_MAP[direction]}`}
            data-cropper-thumb="true"
            onMouseDown={_handleThumbMouseDown.bind(undefined, direction)}
            onTouchStart={_handleThumbMouseDown.bind(undefined, direction)}
        />
    ));

    return (
        <div
            className="eds-image-cropper__porthole"
            style={formatPercentCropMaskForStyle(percentCropMask)}
            onMouseDown={_handlePortholeMouseDown}
            onTouchStart={_handlePortholeMouseDown}
        >
            {thumbs}
        </div>
    );
    /* eslint-enable jsx-a11y/no-static-element-interactions */
};

export default class ImageCropper extends Component {
    static propTypes = {
        /**
         * imageSrc is passed as the src attribute of the image to be cropped.
         * This should be a valid image URL.
         */
        imageSrc: PropTypes.string.isRequired,

        /**
         * The callback function that will be invoked when the user
         * finishes changing the crop mask.
         */
        onCropDone: PropTypes.func,

        /**
         * The callback function that will be invoked while the user
         * is dragging or resizing the crop mask.
         */
        onCropChanging: PropTypes.func,

        /**
         * Use this to populate a crop mask.  Useful when editing an existing mask.
         */
        cropMask: CROP_MASK_PROP_TYPE,

        /**
         * imageCrossOrigin is passed as the crossOrigin attribute of the
         * underlying image tag.
         */
        imageCrossOrigin: PropTypes.string,

        /**
         * Alt attribute provided to the underlying image tag.
         */
        imageAltText: translationPropType,

        /**
         * Desired aspect ratio of the resulting crop.
         */
        aspectRatio: PropTypes.number,

        /**
         * Certain aspect ratios are impossible to enforce exactly when using integers.
         * ImageCropper will attempt to fix inexact mask ratios by subtracting pixels from
         * the user-defined mask, up to a maximum subtraction threshold.  Use this prop
         * to define that treshhold.  For example, if using 2/1 aspect ratio, you can
         * set aspectRatioMaxChunkSize to 1 to allow subtraction of 1 pixel to fix
         * inexact results.  If using 3/1, set aspectRatioMaxChunkSize to 2.
         */
        aspectRatioMaxChunkSize: PropTypes.number,
    };

    static defaultProps = {
        onCropDone: noop,
        onCropChanging: noop,
        imageAltText: '',
        aspectRatioMaxChunkSize: 10,
    };

    constructor(props, context) {
        super(props, context);

        // store references to bound handlers so they can be removed
        this._boundHandlePortholeMouseMove =
            this._handlePortholeMouseMove.bind(this);
        this._boundHandlePortholeMouseUp =
            this._handlePortholeMouseUp.bind(this);
        this._boundHandleThumbMouseMove = this._handleThumbMouseMove.bind(this);
        this._boundHandleThumbMouseUp = this._handleThumbMouseUp.bind(this);
    }

    state = {
        naturalWidth: 0,
        naturalHeight: 0,
        cropMask: undefined,
    };

    UNSAFE_componentWillReceiveProps(nextProps) {
        const { naturalWidth, naturalHeight } = this.state;
        let { cropMask } = this.state;
        const isImageLoaded = !!(naturalWidth && naturalHeight);
        const { cropMask: nextCropMask } = nextProps;
        const shouldUpdate = shouldUpdateCropMask(nextProps, this.props);

        if (
            nextCropMask &&
            cropMaskDidChange(nextCropMask, this.props.cropMask)
        ) {
            cropMask = nextCropMask;
        }

        if (cropMask && isImageLoaded && shouldUpdate) {
            const {
                aspectRatio,
                aspectRatioMaxChunkSize,
                onCropChanging,
                onCropDone,
            } = nextProps;

            cropMask = constrainCropMask(
                naturalWidth,
                naturalHeight,
                cropMask,
                aspectRatio,
                aspectRatioMaxChunkSize,
            );

            this._finishCrop(cropMask, onCropChanging, onCropDone);
        }
    }

    /**
     * Finalize crop actions.  Accept onCropChanging & onCropDone as params so
     * they may be passed from UNSAFE_componentWillReceiveProps nextProps rather
     * than existing props.
     */
    _finishCrop(cropMask, onCropChangingParam, onCropDoneParam) {
        const onCropChanging = onCropChangingParam || this.props.onCropChanging;
        const onCropDone = onCropDoneParam || this.props.onCropDone;

        this.setState({ cropMask });
        onCropChanging(cropMask);
        onCropDone(cropMask);
    }

    _setImageRef(ref) {
        this._image = ref;
    }

    _handleImageLoaded() {
        const { aspectRatio, aspectRatioMaxChunkSize } = this.props;
        let { cropMask } = this.props;
        const { naturalWidth, naturalHeight } = this._image;

        if (!cropMask) {
            cropMask = getDefaultCropMask(
                naturalWidth,
                naturalHeight,
                aspectRatio,
                aspectRatioMaxChunkSize,
            );
        }

        cropMask = constrainCropMask(
            naturalWidth,
            naturalHeight,
            cropMask,
            aspectRatio,
            aspectRatioMaxChunkSize,
        );

        this.setState({
            naturalWidth,
            naturalHeight,
        });
        this._finishCrop(cropMask);
    }

    _getScale() {
        let scale = 1;

        // sometimes scale is requested before image loads
        if (this._image && this._image.naturalWidth) {
            const { clientWidth, naturalWidth } = this._image;

            scale = getScale(clientWidth, naturalWidth);
        }
        return scale;
    }

    _handlePortholeMouseDown(dragStartPosition) {
        const { cropMask } = this.state;

        this._portholeMoveContext = {
            dragStartPosition,
            cropMask,
        };

        addMoveListener(this._boundHandlePortholeMouseMove);
        addActionEndListener(this._boundHandlePortholeMouseUp);
    }

    _handlePortholeMouseMove(event) {
        event.preventDefault();

        const { dragStartPosition, cropMask: dragStartCrop } =
            this._portholeMoveContext;
        const { onCropChanging } = this.props;
        const { naturalWidth, naturalHeight } = this.state;
        const scale = this._getScale();
        const cropMask = getNextMovedCropMask(
            dragStartPosition,
            dragStartCrop,
            getEventPosition(event),
            scale,
            naturalWidth,
            naturalHeight,
        );

        this.setState({ cropMask });
        onCropChanging(cropMask);
    }

    _handlePortholeMouseUp(event) {
        event.preventDefault();

        removeMoveListener(this._boundHandlePortholeMouseMove);
        removeActionEndListener(this._boundHandlePortholeMouseUp);

        delete this._portholeMoveContext;

        const { cropMask } = this.state;

        this._finishCrop(cropMask);
    }

    _handleThumbMouseDown(dragStartPosition, direction) {
        const { cropMask } = this.state;

        this._handleMoveContext = {
            dragStartPosition,
            cropMask,
            direction,
        };

        addMoveListener(this._boundHandleThumbMouseMove);
        addActionEndListener(this._boundHandleThumbMouseUp);
    }

    _handleThumbMouseMove(event) {
        event.preventDefault();

        const {
            dragStartPosition,
            cropMask: dragStartCrop,
            direction,
        } = this._handleMoveContext;
        const { aspectRatio, onCropChanging, aspectRatioMaxChunkSize } =
            this.props;
        const { naturalWidth, naturalHeight } = this.state;

        const cropMask = getNextResizedCropMask(
            direction,
            dragStartPosition,
            dragStartCrop,
            getEventPosition(event),
            naturalWidth,
            naturalHeight,
            this._getScale(),
            aspectRatio,
            aspectRatioMaxChunkSize,
        );

        this.setState({ cropMask });
        onCropChanging(cropMask);
    }

    _handleThumbMouseUp(event) {
        event.preventDefault();

        removeMoveListener(this._boundHandleThumbMouseMove);
        removeActionEndListener(this._boundHandleThumbMouseUp);

        delete this._handleMoveContext;

        const { cropMask } = this.state;

        this._finishCrop(cropMask);
    }

    render() {
        const { imageSrc, imageCrossOrigin, imageAltText } = this.props;
        const { naturalWidth, naturalHeight, cropMask } = this.state;
        let percentCropMask = null;

        if (naturalWidth && naturalHeight && cropMask) {
            percentCropMask = cropMaskToPercent(
                naturalHeight,
                naturalWidth,
                cropMask,
            );
        }

        return (
            <div className="eds-image-cropper">
                <ImageCropperMask percentCropMask={percentCropMask} />
                <ImageCropperPorthole
                    percentCropMask={percentCropMask}
                    thumbMouseDown={this._handleThumbMouseDown.bind(this)}
                    portholeMouseDown={this._handlePortholeMouseDown.bind(this)}
                />
                <img
                    src={imageSrc}
                    alt={imageAltText}
                    className="eds-image-cropper__image"
                    crossOrigin={imageCrossOrigin}
                    ref={this._setImageRef.bind(this)}
                    onLoad={this._handleImageLoaded.bind(this)}
                />
            </div>
        );
    }
}
